import { reportMetrics } from './reportMetrics'
import { pageError } from './metrics/creates'
import { getRoute } from './route'

const TAG = '@shopeepay/reporter'

function getDevice() {
  if (typeof window === 'undefined') return 'unknown'
  return window.innerWidth >= 768 ? 'pc' : 'mobile'
}

function getUaToken() {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return 'unknown'
  return navigator.userAgent.split(' ').pop() || 'unknown'
}

// Constructor name of the thrown value (TypeError, ReferenceError, SyntaxError,
// etc.). Falls back to 'Error' so the label is always set — Prometheus drops
// rows with missing labels otherwise.
function getErrorType(event, isPromise) {
  const source = isPromise ? event.reason : event.error
  if (source && typeof source === 'object') {
    if (source.name) return String(source.name)
    if (source.constructor && source.constructor.name) return String(source.constructor.name)
  }
  return isPromise ? 'UnhandledRejection' : 'Error'
}

// Coarse bucket: runtime (uncaught throw) | promise (unhandledrejection) |
// resource_load (an <img>/<script>/<link> failed to load — event.target is an
// element rather than window).
function getErrorCategory(event, isPromise) {
  if (isPromise) return 'promise'
  const target = event.target
  if (target && target !== window && target.tagName) return 'resource_load'
  return 'runtime'
}

// Bundle name extracted from event.filename. e.g.
//   /static/js/vendor.abc123.chunk.js → vendor
//   /static/js/main.def456.js         → main
// Keeps cardinality low (~handful of bundles) while still attributing errors
// to a build artifact. Unknown / inline scripts → 'inline'.
function getSourceBundle(filename) {
  if (!filename) return 'inline'
  const file = String(filename).split('?')[0].split('/').pop() || ''
  const match = file.match(/^([a-zA-Z][a-zA-Z0-9_-]*)/)
  return match ? match[1] : 'other'
}

export function reportJsError(errorMessage, route, extras = {}) {
  if (typeof window === 'undefined') return
  reportMetrics(
    pageError({
      error_message: String(errorMessage).substring(0, 200),
      route,
      device: getDevice(),
      ext1: getUaToken(),
      error_type: extras.error_type || 'Error',
      error_category: extras.error_category || 'runtime',
      source_bundle: extras.source_bundle || 'inline',
    })
  )
}

export function initErrorTracking(maxErrorsPerSession = 50) {
  if (typeof window === 'undefined') return

  let errorCount = 0

  window.addEventListener('error', (event) => {
    if (errorCount >= maxErrorsPerSession) return
    const msg = event.message || ''
    if (msg.includes(TAG) || msg.includes('telemetry')) return
    errorCount++
    reportJsError(msg || 'Unknown error', getRoute(), {
      error_type: getErrorType(event, false),
      error_category: getErrorCategory(event, false),
      source_bundle: getSourceBundle(event.filename),
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (errorCount >= maxErrorsPerSession) return
    const msg = String(event.reason) || ''
    if (msg.includes(TAG) || msg.includes('telemetry')) return
    errorCount++
    reportJsError(msg || 'Unhandled promise rejection', getRoute(), {
      error_type: getErrorType(event, true),
      error_category: 'promise',
      // Promise rejections don't expose a filename; attribute to the bundle
      // that hosted the rejecting code only when the reason is an Error with
      // a stack we can parse cheaply.
      source_bundle: 'inline',
    })
  })
}
